import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    addPending,
    clearTimedOutQueries,
    incrementIncompleteTauCount,
    setTaus,
    setWindDirectionData,
    updateData,
    updateErrorMessage,
    useAllTaus,
    useForecastTime,
    useSelectedSurface,
    useShouldStopFetchingData,
    useTausWithGeoJson
} from "../../features/surface/surfaceSlice";
import {
    ForecastImpactData,
    ImpactSurfaceResponse,
    RequestForecastPayload,
    useGetAvailableSurfacesForTimeQuery,
    useGetWindDirectionDataQuery,
} from "../../services/riskSurface";
import { fetchRiskSurface } from "./RiskViewerUtils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { getComponentForecastTaus, getGeoJSONTaus, getMinMaxTimes } from "../../features/surface/surfaceUtils";
import { setCurrentDay, setMinMaxDataTime } from "../../features/time/TimelineSlice";
import { getDayStart } from "../risk/geoUtils";
import { RouteMetadata } from "../../generated/gpb/api/route";
import { useSelectedRoute } from "../../features/riskViewer/riskViewerSlice";

export const useFetchSurfaces = (
    latestSucceeded: boolean,
    routeMetadata: RouteMetadata | undefined,
    refetchRiskData: ( tauTimeout: boolean ) => void
) =>
/**
 * Custom hook to fetch available surfaces based on the provided forecast time.
 * It handles fetching the data, managing loading and error states, and updating
 * the Redux store with the fetched surface data.
 *
 */
{
    const dispatch = useDispatch();
    const routeId = useSelectedRoute();
    const surfaceName = useSelectedSurface();
    const geoJsons = useTausWithGeoJson();
    const forecastTime = useForecastTime();
    const shouldStopFetchingData = useShouldStopFetchingData();
    const allTaus = useAllTaus();

    const {
        data: components,
        isFetching: isFetchingAvailableSurfaces,
        refetch: refetchSurfaces,
        isUninitialized: surfacesUninitialized,
        isError: isMovedToGlacial
    } = useGetAvailableSurfacesForTimeQuery(
        forecastTime && !shouldStopFetchingData ? { ...forecastTime } : skipToken,
        {
            skip: !forecastTime && ( !latestSucceeded || routeId?.hexString !== undefined ),
        }
    );

    const {
        data: windData,
        refetch: refetchWindDirectionData,
    } = useGetWindDirectionDataQuery(
        forecastTime ? {
            year: parseInt( forecastTime.date.substring( 0, 4 ), 10 ),
            month: parseInt( forecastTime.date.substring( 4, 6 ), 10 ),
            day: parseInt( forecastTime.date.substring( 6, 8 ), 10 ),
            hour: parseInt( forecastTime.hour, 10 )
        } : skipToken
    );

    useEffect( () =>
    {
        if ( windData !== undefined )
        {
            dispatch( setWindDirectionData( windData ) );
        }
    }, [ windData ] );

    useEffect( () =>
    {
        if ( isMovedToGlacial )
        {
            dispatch( updateErrorMessage( "Moved to glacial storage" ) );
        }
    }, [ isMovedToGlacial ] );

    useEffect( () =>
    {
        if ( !surfacesUninitialized )
        {
            refetchSurfaces().catch( ( err ) => console.error( err ) );
            refetchWindDirectionData().catch( ( err ) => console.error( err ) );
        }
    }, [ forecastTime, geoJsons, latestSucceeded, routeId ] );

    const [ isFetching, setIsFetching ] = useState( false );
    const [ isError, setIsError ] = useState( false );

    useEffect( () =>
    {
        if ( !forecastTime || geoJsons.length === 0 ) return;

        const requestPayload: RequestForecastPayload = {
            surface: {
                start: 0,
                limit: geoJsons.length
            },
            time: forecastTime,
            surfaceName
        };

        dispatch( addPending( {
            page: requestPayload.surface,
            surfaceName,
            timestamp: performance.now()
        } ) );

        setIsFetching( true );
        setIsError( false );

        fetchRiskSurface( requestPayload )
            .then( response =>
            {
                if ( !response.ok )
                {
                    return response.json().then( errorData =>
                    {
                        throw new Error( JSON.stringify( errorData.error ) );
                    } );
                }
                return response.json();
            } )
            .then( response =>
            {
                const impactAll: Array<ImpactSurfaceResponse> = response.data.map( ( fc: ForecastImpactData, index: number ) => ( {
                    impactUrls: fc,
                    index
                } ) );

                dispatch( updateData( {
                    surfaceData: impactAll,
                    surfaceName,
                    forecastTime
                } ) );
            } )
            .catch( err =>
            {
                setIsError( true );
                dispatch( incrementIncompleteTauCount( requestPayload ) );
                dispatch( updateErrorMessage( err.toString() ) );
            } )
            .finally( () => setIsFetching( false ) );

    }, [ forecastTime, surfaceName, geoJsons ] );

    useEffect( () =>
    {
        const intervalId = setInterval( () =>
        {
            dispatch( clearTimedOutQueries() );
        }, 14000 );
        return () => clearInterval( intervalId );
    }, [] );

    useEffect( () =>
    {
        const componentTaus = getComponentForecastTaus( surfaceName, components?.impact );
        const geoTaus = getGeoJSONTaus( components, surfaceName );

        if ( componentTaus.length === 0 && !( surfacesUninitialized || isFetchingAvailableSurfaces ) )
        {
            dispatch( updateErrorMessage( "No Impact Surface found on the backend. Data may have been moved to glacial storage." ) );
        }
        else
        {
            dispatch( setTaus( { allTaus: componentTaus, tausWithGeoJson: geoTaus, newForecastTime: forecastTime } ) );
        }
    }, [ surfaceName, components, forecastTime, isFetchingAvailableSurfaces ] );

    useEffect( () =>
    {
        const { minTime, maxTime } = getMinMaxTimes( allTaus, routeMetadata );
        dispatch( setMinMaxDataTime( { min: minTime, max: maxTime } ) );
        dispatch( setCurrentDay( getDayStart( minTime ) ) );
    }, [ allTaus, routeMetadata ] );

    useEffect( () =>
    {
        const intervalId = setInterval( () =>
        {
            refetchRiskData( true );
            //presigned urls expire every 60 minutes, so this should ensure we refetch them prior to expiration (every 58 minutes)
            // (58 minutes -> ms: 1000 ms/s * 60 s/minute * 58 minutes)
        }, 1000 * 60 * 58 );

        return () => clearInterval( intervalId ); // this should be triggered every time the user switches forecast surfaces, resetting the timer
    }, [ isFetchingAvailableSurfaces ] );

    return {
        components,
        isFetching,
        isError,
        surfaceName
    };
};
